/**
 * BundlePicker — swaps between sibling bundle products without a full page load.
 *
 * The bundle selector is a set of ordinary anchors (see `snippets/pdp-bundle-group.liquid`):
 * every gun/package combination is a separate product, so picking a swatch genuinely means
 * "navigate to a different product". That markup is the baseline and it is complete on its own —
 * this component only upgrades the click, so the selector keeps working with JS disabled or still
 * loading, and every combination stays a crawlable URL.
 *
 * The upgrade is the same technique Horizon uses for combined listings in `variant-picker.js`:
 * fetch the target product page, morph its `<main>` over the current one, then push the URL. That
 * swaps gallery, price, description, accordion and add-to-cart in one pass — all of which belong to
 * the sibling product — while leaving the scroll position alone, which is the whole point.
 *
 * `variant-picker` is deliberately not reused for this. It bails out unless the fetched document
 * contains `variant-picker script[type="application/json"]`, which a bundle product has no reason
 * to render, and its `selectedOptionsValues` getter throws unless every checked input carries a
 * `data-option-value-id` — then sends those ids as `option_values=` on the request, which is
 * meaningless when the destination is a different product. Satisfying it would mean emitting fake
 * variant data to keep it quiet.
 *
 * Any failure falls back to a real navigation, so a fetch error costs a page load, never a dead
 * swatch.
 *
 * DOM contract:
 *   <bundle-picker>              — the host, wrapping both axis groups
 *   a[data-bundle-option]        — one swatch; its href is the destination product
 *   [aria-current="true"]        — the swatch already being shown; clicking it is a no-op
 */

import { morph, MORPH_OPTIONS } from "@theme/morph";

const OPTION_SELECTOR = "a[data-bundle-option]";

class BundlePicker extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.addEventListener("click", this.#handleClick);
    window.addEventListener("popstate", this.#handlePopState);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#handleClick);
    window.removeEventListener("popstate", this.#handlePopState);
    this.#controller?.abort();
  }

  /**
   * Modified clicks and middle clicks are left alone — "open in new tab" on a swatch is a
   * reasonable thing to want, and it only works if the anchor stays an anchor.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    const link = target instanceof Element ? target.closest(OPTION_SELECTOR) : null;
    if (!link || !this.contains(link)) return;

    // The selected swatch links to the product already on screen. Refetching it would be a
    // no-op morph and a wasted request.
    event.preventDefault();
    if (link.getAttribute("aria-current") === "true") return;

    this.#swap(/** @type {HTMLAnchorElement} */ (link).href, true);
  };

  /**
   * Back and forward have to morph too, otherwise the address bar and the page disagree.
   */
  #handlePopState = () => {
    this.#swap(window.location.href, false);
  };

  /**
   * @param {string} url - The sibling bundle product's URL
   * @param {boolean} push - Whether to add a history entry; false when replaying one
   */
  async #swap(url, push) {
    const main = document.querySelector("main");
    if (!main) {
      window.location.assign(url);
      return;
    }

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    main.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Bundle swap failed with ${response.status}`);

      const document_ = new DOMParser().parseFromString(await response.text(), "text/html");
      const next = document_.querySelector("main");
      if (!next) throw new Error("Bundle swap response has no <main>");

      // Pushed after the morph, so a thrown morph leaves the address bar agreeing with the page
      // that is still on screen.
      morph(main, next, MORPH_OPTIONS);
      if (push) history.pushState({}, "", url);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      console.error(error);
      window.location.assign(url);
    } finally {
      main.removeAttribute("aria-busy");
    }
  }
}

if (!customElements.get("bundle-picker")) {
  customElements.define("bundle-picker", BundlePicker);
}

export default BundlePicker;
